import { Input, InputProps } from "@chakra-ui/react";
import _ from "lodash";
import { useEffect, useRef, useState } from "react";

type Props = Omit<InputProps, "onChange"> & {
  value: string | undefined;
  onDebouncedChange: (value: string) => void;
  delay?: number;
};

const DebouncedInput = ({
  value,
  onDebouncedChange,
  delay = 400,
  ...inputProps
}: Props): JSX.Element => {
  const [localValue, setLocalValue] = useState(value ?? "");
  const callbackRef = useRef(onDebouncedChange);
  const debouncedCallback = useRef<_.DebouncedFunc<(val: string) => void> | null>(null);

  // Lazy-initialize so the debounced function exists before any interaction
  if (!debouncedCallback.current) {
    debouncedCallback.current = _.debounce(
      (val: string) => callbackRef.current(val),
      delay
    );
  }

  useEffect(() => {
    callbackRef.current = onDebouncedChange;
  }, [onDebouncedChange]);

  // Recreate the debounced function when delay changes,
  // cancelling any pending call from the previous function first.
  useEffect(() => {
    const fn = _.debounce((val: string) => callbackRef.current(val), delay);
    debouncedCallback.current?.cancel();
    debouncedCallback.current = fn;
    return () => fn.cancel();
  }, [delay]);

  // Sync display value and cancel any pending debounce on external reset
  // (e.g. merchant change, filter clear)
  useEffect(() => {
    setLocalValue(value ?? "");
    debouncedCallback.current?.cancel();
  }, [value]);

  return (
    <Input
      {...inputProps}
      value={localValue}
      onChange={(e) => {
        setLocalValue(e.target.value);
        debouncedCallback.current?.(e.target.value);
      }}
    />
  );
};

export default DebouncedInput;
